import { useState } from "react";
import { InfoRow } from "@components/info-row";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { AdmPlan } from "@custom-types/base-types";
import { Dialog, Dropdown, DropdownOption, Text, useToast } from "@flat-ui";
import { getPlanByIdSelector } from "@store/plans/plans-selector";
import { updatePlan } from "@store/plans/plans-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { formatPlanName } from "@utils/data-display";
import {
  isPlanApp,
  PLAN_APP_NAMES,
  PLAN_APPS,
  PlanApp,
  planAppEditabilityOf,
  PlanAppSettings,
  planAppSettingsOf,
  planAppUpdateOf,
} from "@utils/product/plan-apps";
import { TableType } from "@utils/track-event/event-types";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

const appOptions: DropdownOption[] = PLAN_APPS.map((app) => ({
  key: app,
  value: app,
  label: PLAN_APP_NAMES[app],
}));

interface Props {
  /** The ID of the plan to show and change the applications of */
  planId: AdmPlan["id"];

  /** The type of the table, used for tracking events */
  tableType: TableType;
}

/**
 * Displays the viewer and dashboard applications of a plan, as dropdowns for the ones that can be
 * changed. A change is only applied to the plan after the user confirms it.
 */
export function PlanAppRows({
  planId,
  tableType,
}: Props): React.JSX.Element | null {
  const plan = useAppSelector(getPlanByIdSelector(planId));
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandler();
  const [pendingSettings, setPendingSettings] =
    useState<PlanAppSettings | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const appSettings = plan ? planAppSettingsOf(plan) : null;
  if (!plan || !appSettings) {
    return null;
  }

  const { canChangeViewer, canChangeDashboard } = planAppEditabilityOf(plan);

  function requestChange(changed: Partial<PlanAppSettings>): void {
    if (!appSettings) {
      return;
    }
    const updated = { ...appSettings, ...changed };
    const isSameAsCurrent =
      updated.defaultViewerApp === appSettings.defaultViewerApp &&
      updated.defaultDashboardApp === appSettings.defaultDashboardApp;

    if (!isSameAsCurrent) {
      setPendingSettings(updated);
    }
  }

  async function applyChange(): Promise<void> {
    if (!plan || !pendingSettings) {
      return;
    }

    trackEvent({
      name: PlanEvents.updatePlanApps,
      props: {
        location: tableType,
        viewerApp: pendingSettings.defaultViewerApp,
        dashboardApp: pendingSettings.defaultDashboardApp,
      },
    });

    try {
      setIsUpdating(true);
      await dispatch(
        updatePlan({
          planId: plan.id,
          updatedProps: {
            type: "updatingFeatures",
            ...planAppUpdateOf(plan, pendingSettings),
          },
        })
      ).unwrap();

      openToast({ title: "The applications of the plan were updated" });
      setPendingSettings(null);
    } catch (error) {
      handleErrorWithToast({
        id: `updating-plan-apps-${Date.now().toString()}`,
        title: "Could not update the applications of the plan",
        error,
      });
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <>
      <InfoRow
        label="Viewer Application"
        info={
          canChangeViewer ? (
            <AppDropdown
              value={appSettings.defaultViewerApp}
              ariaLabel="Viewer Application"
              onChange={(defaultViewerApp) =>
                requestChange({ defaultViewerApp })
              }
            />
          ) : (
            PLAN_APP_NAMES[appSettings.defaultViewerApp]
          )
        }
      />

      <InfoRow
        label="Dashboard Application"
        info={
          canChangeDashboard ? (
            <AppDropdown
              value={appSettings.defaultDashboardApp}
              ariaLabel="Dashboard Application"
              onChange={(defaultDashboardApp) =>
                requestChange({ defaultDashboardApp })
              }
            />
          ) : (
            PLAN_APP_NAMES[appSettings.defaultDashboardApp]
          )
        }
      />

      {pendingSettings && (
        <Dialog
          open={true}
          title={`Change applications of "${formatPlanName(plan)}"`}
          onCancel={() => setPendingSettings(null)}
          onConfirm={applyChange}
          showSpinner={isUpdating}
          confirmText="Yes, change"
        >
          <Text variant="bodyL">
            Every member covered by this plan will open these applications:
          </Text>
          <ChangeLine
            label="Viewer"
            from={appSettings.defaultViewerApp}
            to={pendingSettings.defaultViewerApp}
          />
          <ChangeLine
            label="Dashboard"
            from={appSettings.defaultDashboardApp}
            to={pendingSettings.defaultDashboardApp}
          />
          {pendingSettings.defaultViewerApp === "sphereXg" && (
            <Text variant="bodyM" sx={{ mt: 2 }}>
              With Sphere XG as the viewer, all links pointing to HoloBuilder
              and the HoloBuilder WebEditor are redirected to Sphere XG, and the
              workspace is shown as migrated to Sphere XG.
            </Text>
          )}
        </Dialog>
      )}
    </>
  );
}

interface AppDropdownProps {
  /** The application currently picked */
  value: PlanApp;

  /** The accessible name of the dropdown */
  ariaLabel: string;

  /** Called with the application the user picks */
  onChange(app: PlanApp): void;
}

/** A compact dropdown to pick one of the applications a plan can open */
function AppDropdown({
  value,
  ariaLabel,
  onChange,
}: AppDropdownProps): React.JSX.Element {
  // The flat-ui dropdown only closes its menu once the value changes, which here waits for the
  // confirmation, so it is remounted after every pick to close the menu right away
  const [pickCount, setPickCount] = useState<number>(0);

  return (
    <Dropdown
      key={pickCount}
      value={value}
      options={appOptions}
      variant="underlined"
      aria-label={ariaLabel}
      onChange={({ target: { value: picked } }) => {
        setPickCount((count) => count + 1);
        if (isPlanApp(picked)) {
          onChange(picked);
        }
      }}
    />
  );
}

interface ChangeLineProps {
  /** Which application is changed */
  label: string;

  /** The application the plan opens now */
  from: PlanApp;

  /** The application the plan opens after the change */
  to: PlanApp;
}

/** One line of the confirmation, showing an application before and after the change */
function ChangeLine({ label, from, to }: ChangeLineProps): React.JSX.Element {
  const text =
    from === to
      ? `${label}: ${PLAN_APP_NAMES[from]} (unchanged)`
      : `${label}: ${PLAN_APP_NAMES[from]} → ${PLAN_APP_NAMES[to]}`;

  return (
    <Text variant="bodyM" sx={{ mt: 1 }}>
      {text}
    </Text>
  );
}
